import * as d3 from "d3";
import { mainToMiddle, setupSvg, treeFit } from "./View.handlers.js";
import { createPath } from "./elements/Link.js";
import { createLinks } from "../CalculateTree/createLinks.js";
import { Card as CardDefault } from "./elements/Card.js";
import { calculateEnterAndExitPositions } from "../CalculateTree/CalculateTree.handlers.js";
import { shiftSubtree } from "../CalculateTree/CalculateTree.js";


export default function d3AnimationView({
  store,
  cont,
  Card,
  bg_color,
  backgroundImageUrl,
  fitTree
}) {
  function addAnniversaryFilters(svg) {
  const defs = svg.append("defs");

  // Pink glow for link
  const glow = defs.append("filter").attr("id", "anniversary-glow");
  glow.append("feGaussianBlur").attr("stdDeviation", 4).attr("result", "coloredBlur");
  const feMerge = glow.append("feMerge");
  feMerge.append("feMergeNode").attr("in", "coloredBlur");
  feMerge.append("feMergeNode").attr("in", "SourceGraphic");

  // White sparkle glow
  const sparkle = defs.append("filter").attr("id", "sparkle-glow");
  sparkle.append("feGaussianBlur").attr("stdDeviation", 2).attr("result", "blur");
  const feMerge2 = sparkle.append("feMerge");
  feMerge2.append("feMergeNode").attr("in", "blur");
  feMerge2.append("feMergeNode").attr("in", "SourceGraphic");
}

  const svg = createSvg(bg_color, backgroundImageUrl);
    addAnniversaryFilters(d3.select(svg));
  setupSvg(svg, store.state.zoom_polite);
  svg.style.touchAction = "none";
  svg.style.userSelect = "none";
  svg.style.webkitUserSelect = "none";

  let dragHistory = [];
  let dragEnabled = false; 

  const resetBtn = addResetButton(svg);
  const undoBtn = addUndoButton(svg);

  window.addEventListener("toggleDrag", (e) => {
    dragEnabled = e.detail; 
    updateView(); 
  });

  return { update: updateView, svg, setCard: (card) => (Card = card) };

  function updateView(props) {
    if (!props) props = {};

    const DRAG_THRESHOLD = 3;
    const tree = store.state.tree,
      view = d3.select(svg).select(".view"),
      tree_position = props.tree_position || "fit",
      transition_time = props.hasOwnProperty("transition_time")
        ? props.transition_time
        : 2000;

    updateCards(tree);
    updateLinks(tree.removedIds);
    window.tree=tree
    if (props.initial) {
      mainToMiddle({
        datum: tree.data[0],
        svg,
        svg_dim: svg.getBoundingClientRect(),
        scale: 0.7,
        transition_time
      });
    } else if (tree_position === "fit") {
      treeFit({
        svg,
        svg_dim: svg.getBoundingClientRect(),
        tree_dim: tree.dim,
        transition_time
      });
    } else if (tree_position === "main_to_middle") {
      mainToMiddle({
        datum: tree.data[0],
        svg,
        svg_dim: svg.getBoundingClientRect(),
        scale: props.scale,
        transition_time
      });
    } else if (tree_position === "inherit") {
      treeFit({
        svg,
        svg_dim: svg.getBoundingClientRect(),
        tree_dim: tree.dim,
        transition_time
      });
    }

    return true;
    function updateLinks(removedIds) {
      const links_data = tree.data
          .filter((d) => !d.data.data.hidden)
          .reduce(
            (acc, d) =>
              acc.concat(
                createLinks({
                  d,
                  tree: tree.data,
                  is_vertical: true,
                  relationshipPathNodes: store.relationshipPathNodes,
                  removedIds: removedIds,
                  spouseNodeId: store.state.spouseNodeId
                })
              ),
            []
          ),
        link = view
          .select(".links_view")
          .selectAll("path.link")
          .data(links_data, (d) => d.id),
        link_exit = link.exit(),
        link_enter = link.enter().append("path").attr("class", "link"),
        link_update = link_enter.merge(link);

      link_exit.each(linkExit);
      link_enter.each(linkEnter);
      link_update.each(linkUpdate);

      function blinkPath(path) {
        path
          .transition()
          .duration(500)
          .style("opacity", 0)
          .transition()
          .duration(500)
          .style("opacity", 1)
          .on("end", function () {
            blinkPath(d3.select(this));
          });
      }
    function getLinkMidpoint(d) {
  const coords = d.d;   // the array [ [x1,y1], [x2,y2] ]

  const sx = coords[0][0];
  const sy = coords[0][1];
  const tx = coords[1][0];
  const ty = coords[1][1];

  return {
    x: (sx + tx) / 2,
    y: (sy + ty) / 2
  };
}

/* ----------------------------------------------------------
   ANNIVERSARY DECORATIONS FOR SPOUSE LINKS
---------------------------------------------------------- */

function decorateAnniversaryLink(path, d) {
  if (!d.isSpouse) return;

  const parent = d3.select(path.node().parentNode);
  const mid = getLinkMidpoint(d);

  /* ----------------------------------------------------------
     1️⃣  GLOWING ANIMATED LINK OUTLINE
  ---------------------------------------------------------- */
  path
    .attr("stroke", "#ff3b7d")
    .attr("stroke-width", 3)
    .style("filter", "url(#anniversary-glow)");

  /* ----------------------------------------------------------
     2️⃣  PULSING HEART AT MIDPOINT
  ---------------------------------------------------------- */
  let heart = parent.select(".anniversary-heart");

  if (heart.empty()) {
    heart = parent
      .append("text")
      .attr("class", "anniversary-heart")
      .attr("text-anchor", "middle")
      .attr("dy", "-4")
      .style("font-size", "22px")
      .style("pointer-events", "none")
      .text("❤️");
  }

  heart
    .attr("x", mid.x)
    .attr("y", mid.y)
    .transition()
    .duration(1200)
    .ease(d3.easeSin)
    .attrTween("font-size", () => {
      return (t) => `${20 + Math.sin(t * Math.PI) * 6}px`;
    })
    .on("end", function repeat() {
      d3.select(this).transition().duration(1200).ease(d3.easeSin)
        .attrTween("font-size", () => {
          return (t) => `${20 + Math.sin(t * Math.PI) * 6}px`;
        })
        .on("end", repeat);
    });

  /* ----------------------------------------------------------
     3️⃣  FLOATING HEARTS ✨
  ---------------------------------------------------------- */
  triggerFloatingHearts(parent, mid);

  /* ----------------------------------------------------------
     4️⃣  CONFETTI BURST 🎉
  ---------------------------------------------------------- */
  confettiBurst(parent, mid);

  /* ----------------------------------------------------------
     5️⃣  SPARKLE TRAIL ✨
  ---------------------------------------------------------- */
  sparkleTrailAlongPath(parent, path.node(), d);
}

/* ----------------------------------------------------------
   FLOATING HEARTS
---------------------------------------------------------- */

function triggerFloatingHearts(parent, mid) {
  for (let i = 0; i < 6; i++) {
    const h = parent
      .append("text")
      .text("💖")
      .attr("x", mid.x + (Math.random() * 40 - 20))
      .attr("y", mid.y + (Math.random() * 10 - 5))
      .style("font-size", `${12 + Math.random() * 6}px`)
      .style("opacity", 1);

    h.transition()
      .duration(2000 + Math.random() * 600)
      .attr("y", mid.y - 40 - Math.random() * 30)
      .style("opacity", 0)
      .remove();
  }
}

/* ----------------------------------------------------------
   CONFETTI BURST FUNCTION
---------------------------------------------------------- */

function confettiBurst(parent, mid) {
  const colors = ["#ff4e91", "#ffb3d6", "#ffd1e8", "#ff6cb3"];

  for (let i = 0; i < 14; i++) {
    const angle = Math.random() * 2 * Math.PI;
    const dist = 30 + Math.random() * 30;

    const x = mid.x + Math.cos(angle) * dist;
    const y = mid.y + Math.sin(angle) * dist;

    const c = parent
      .append("circle")
      .attr("cx", mid.x)
      .attr("cy", mid.y)
      .attr("r", 3)
      .attr("fill", colors[i % colors.length])
      .style("opacity", 1);

    c.transition()
      .duration(1500)
      .attr("cx", x)
      .attr("cy", y)
      .style("opacity", 0)
      .remove();
  }
}

/* ----------------------------------------------------------
   SPARKLE TRAIL ALONG SPOUSE LINK
---------------------------------------------------------- */

function sparkleTrailAlongPath(parent, pathNode, d) {
  const pathLength = pathNode.getTotalLength();

  (function animateSparkle() {
    const sparkle = parent
      .append("circle")
      .attr("r", 4)
      .attr("fill", "#fff8")
      .style("filter", "url(#sparkle-glow)");

    sparkle
      .transition()
      .duration(2500)
      .ease(d3.easeSin)
      .attrTween("transform", () => {
        return (t) => {
          const p = pathNode.getPointAtLength(t * pathLength);
          return `translate(${p.x}, ${p.y})`;
        };
      })
      .style("opacity", 0)
      .remove()
      .on("end", animateSparkle);
  })();
}

/* ----------------------------------------------------------
   SVG FILTERS (Glow Effects)
---------------------------------------------------------- */

function linkEnter(d) {
  d3.select(this)
    .attr("fill", "none")
    .attr("stroke", d.isRelationshipPath ? "#b873b2" : "#1D3C78")
    .attr("stroke-width", d.isRelationshipPath ? 4 : 1)
    .attr("d", createPath(d, true))
    .style("opacity", 0)
    .transition()
    .duration(500)
    .style("opacity", 1)
    .on("end", function () {

      // ❤️ Correct middle position
      if (d.isSpouse) {
         decorateAnniversaryLink(d3.select(this), d);
      }

      if (d.isRelationshipPath) {
        const selectedPath = d3.select(this);
        blinkPath(d3.select(this));

        setTimeout(() => {
          if (!selectedPath.empty()) {
            selectedPath
              .interrupt()
              .style("opacity", 1)
              .attr("stroke", "#1D3C78")
              .attr("stroke-width", 1);
          }
        }, 10000);
      }
    });
}

   function linkUpdate(d) {
  const path = d3.select(this);
  const delay = calculateDelay(d);

  path
    .transition("path")
    .duration(transition_time)
    .delay(delay)
    .attr("d", createPath(d))
    .style("opacity", 1)
    .attr("stroke", d.isRelationshipPath ? "#b873b2" : "#1D3C78")
    .attr("stroke-width", d.isRelationshipPath ? 4 : 1)
    .on("end", function () {

      // ❤️ Update or add heart at midpoint
      if (d.isSpouse) {
            decorateAnniversaryLink(d3.select(this), d);
      }

      if (d.isRelationshipPath) {
        const selectedPath = d3.select(this);
        blinkPath(selectedPath);

        setTimeout(() => {
          if (!selectedPath.empty()) {
            selectedPath
              .interrupt()
              .style("opacity", 1)
              .attr("stroke", "#1D3C78")
              .attr("stroke-width", 1);
            store.relationshipPathNodes = [];
          }
        }, 10000);
      }
    });
}

      function linkExit(d) {
        const path = d3.select(this);
        path.transition("op").duration(800).style("opacity", 0);
        path
          .transition("path")
          .duration(transition_time)
          .attr("d", createPath(d, true))
          .on("end", () => path.remove());
      }
    }

   
    function updateCards(tree) {
      const card = view
          .select(".cards_view")
          .selectAll("g.card_cont")
          .data(
            tree.data.filter((d) => !d.data.data.hidden),
            (d) => d.data.id
          ),
        card_exit = card.exit(),
        card_enter = card.enter().append("g").attr("class", "card_cont"),
        card_update = card_enter.merge(card);

      card_exit.each((d) => calculateEnterAndExitPositions(d, false, true));
      card_enter.each((d) => calculateEnterAndExitPositions(d, true, false));

      card_exit.each(cardExit);
      card.each(cardUpdateNoEnter);
      card_enter.each(cardEnter);
      card_update.each(cardUpdate);

      if (dragEnabled) {
        applyDrag(card_enter, tree);
        applyDrag(card_update, tree);
      } else {
        card_enter.on(".drag", null);
        card_update.on(".drag", null);
      }

      function cardEnter(d) {
        d3.select(this)
          .attr("transform", `translate(${d._x}, ${d._y})`)
          .style("opacity", 0)
          .node()
          .appendChild(CardElement(this, d));
      }

      function cardUpdateNoEnter(d) {}

      function cardUpdate(d) {
        this.innerHTML = "";
        this.appendChild(CardElement(this, d));
        const delay = calculateDelay(d);
        d3.select(this)
          .transition()
          .duration(transition_time)
          .delay(delay)
          .attr("transform", `translate(${d.x}, ${d.y})`)
          .style("opacity", 1);
      }

      function cardExit(d) {
        const g = d3.select(this);
        g.transition()
          .duration(transition_time)
          .style("opacity", 0)
          .attr("transform", `translate(${d._x}, ${d._y})`)
          .on("end", () => g.remove());
      }

      function CardElement(node, d) {
        if (Card) return Card({ node, d });
        else return CardDefault({ store, svg })({ node, d });
      }
    }


    function dragStarted(event, d) {
      const coords = getClientFromEvent(event);
      d.__dragStartClient = { x: coords.clientX, y: coords.clientY, t: Date.now() };
      d.__dragMoved = false;
      d.__dragging = true;

      const prevState = store.state.tree.data.map((n) => ({
        id: n.data.id,
        x: n.x,
        y: n.y
      }));
      dragHistory.push(prevState);

      if (!d.originalPos) {
        d.originalPos = { x: d.x, y: d.y };
      }
      d3.select(this).raise().classed("active", true);
    }

    function dragged(event, d, tree) {
      const coords = getClientFromEvent(event);
      const start = d.__dragStartClient || coords;
      const dxFromStart = Math.abs(coords.clientX - start.x);
      const dyFromStart = Math.abs(coords.clientY - start.y);
      const dist = Math.hypot(dxFromStart, dyFromStart);

      if (!d.__dragMoved && dist > DRAG_THRESHOLD) d.__dragMoved = true;

      if (d.__dragMoved) {
        const dx =
          typeof event.dx === "number" ? event.dx : coords.clientX - start.x;
        const dy =
          typeof event.dy === "number" ? event.dy : coords.clientY - start.y;

        shiftSubtree(d, dx, dy, tree);

        d3.select(this).attr("transform", `translate(${d.x}, ${d.y})`);

        d3.selectAll("g.card_cont").attr(
          "transform",
          (n) => `translate(${n.x}, ${n.y})`
        );

        d3.selectAll("path.link").attr("d", createPath);
      }
    }

    function dragEnded(event, d) {
      d3.select(this).classed("active", false);

      if (!d.__dragMoved) {
        try {
          const coords = getClientFromEvent(event);
          const el =
            document.elementFromPoint(coords.clientX, coords.clientY) || this;
          const clickEvent = new MouseEvent("click", {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX: coords.clientX,
            clientY: coords.clientY
          });
          el.dispatchEvent(clickEvent);
        } catch (e) {
          if (typeof store.nodeClickHandler === "function") {
            try {
              store.nodeClickHandler(d, this);
            } catch (_) {}
          }
        }
      } else {
        updateCards(store.state.tree);
        updateLinks(store.state.tree.removedIds);
        resetBtn.style("display", "block");
        undoBtn.style("display", "block");
      }

      d.__dragging = false;
      d.__dragStartClient = null;
      d.__dragMoved = false;
    }

    function applyDrag(selection, tree) {
      selection.call(
        d3
          .drag()
          .on("start", dragStarted)
          .on("drag", (event, d) => dragged(event, d, tree))
          .on("end", dragEnded)
      );
    }

    function getClientFromEvent(event) {
      const src = event.sourceEvent || event;
      if (src.touches && src.touches.length > 0)
        return { clientX: src.touches[0].clientX, clientY: src.touches[0].clientY };
      if (src.changedTouches && src.changedTouches.length > 0)
        return {
          clientX: src.changedTouches[0].clientX,
          clientY: src.changedTouches[0].clientY
        };
      return { clientX: src.clientX || 0, clientY: src.clientY || 0 };
    }

    function calculateDelay(d) {
      if (!props.initial) return 0;
      const delay_level = 1,
        ancestry_levels = Math.max(...tree.data.map((d) => (d.is_ancestry ? d.depth : 0)));
      let delay = d.depth * delay_level;
      if ((d.depth !== 0 || !!d.spouse) && !d.is_ancestry) {
        delay += ancestry_levels * delay_level;
        if (d.spouse) delay += delay_level;
        delay += d.depth * delay_level;
      }
      return delay;
    }
  }

  function addResetButton(svg) {
    let resetBtn = d3.select(svg).select(".reset_button");
    if (resetBtn.empty()) {
      const bbox = svg.getBoundingClientRect();
      resetBtn = d3
        .select(svg)
        .append("g")
        .attr("class", "reset_button cursor-pointer")
        .attr("transform", `translate(${bbox.width - 60}, ${bbox.height - 60})`)
        .style("display", "none");
      resetBtn
        .append("circle")
        .attr("r", 22)
        .attr("cx", 20)
        .attr("cy", 20)
        .attr("fill", "rgb(11, 138, 183)")
        .attr("stroke", "#fff")
        .attr("stroke-width", 2);
      resetBtn
        .append("text")
        .attr("x", 20)
        .attr("y", 26)
        .attr("text-anchor", "middle")
        .style("fill", "#fff")
        .style("font-size", "20px")
        .style("font-weight", "bold")
        .text("⟳");
      resetBtn.on("click", () => {
        store.state.data = JSON.parse(JSON.stringify(store.state.initialData));
        store.update.tree({});
        updateView({ initial: true });
        undoBtn.style("display", "none");
        dragHistory = [];
        d3.selectAll(".card_hide_path").style("display", "none");
        resetBtn.style("display", "none");
      });
    }
    return resetBtn;
  }

  function addUndoButton(svg) {
    const bbox = svg.getBoundingClientRect();
    let undoBtn = d3.select(svg).select(".undo_button");
    if (undoBtn.empty()) {
      undoBtn = d3
        .select(svg)
        .append("g")
        .attr("class", "undo_button cursor-pointer")
        .attr("transform", `translate(${bbox.width - 120}, ${bbox.height - 60})`)
        .style("display", "none");
      undoBtn
        .append("circle")
        .attr("r", 22)
        .attr("cx", 20)
        .attr("cy", 20)
        .attr("fill", "rgb(11, 138, 183)")
        .attr("stroke", "#fff")
        .attr("stroke-width", 2);
      undoBtn
        .append("text")
        .attr("x", 20)
        .attr("y", 26)
        .attr("text-anchor", "middle")
        .style("fill", "#fff")
        .style("font-size", "20px")
        .style("font-weight", "bold")
        .text("↶");
      undoBtn.on("click", () => {
        if (dragHistory.length === 0) return;
        const lastState = dragHistory.pop();
        store.state.tree.data.forEach((d) => {
          const prev = lastState.find((p) => p.id === d.data.id);
          if (prev) {
            d.x = prev.x;
            d.y = prev.y;
          }
        });
        updateView({ initial: true });
        const isInitial = store.state.tree.data.every((d) => {
          const orig = store.state.initialData.find((n) => n.id === d.data.id);
          return orig && orig.x === d.x && orig.y === d.y;
        });
        if (dragHistory.length === 0 || isInitial) {
          undoBtn.style("display", "none");
        }
      });
    }
    return undoBtn;
  }

  function createSvg(bg_color, backgroundImageUrl) {
    if (!backgroundImageUrl) backgroundImageUrl = "";
    const svg_dim = cont.getBoundingClientRect(),
      svg_html = `
        <svg class="main_svg" width="${svg_dim.width}" height="${svg_dim.height}">
          <image class="background_image" href="${backgroundImageUrl}" x="0" y="0"
            opacity="0.3" width="${svg_dim.width}" height="${svg_dim.height}"
            preserveAspectRatio="xMidYMid slice"></image>
          <rect width="${svg_dim.width}" height="${svg_dim.height}" fill="${bg_color}" />
          <g class="view" id="tree-view">
            <g class="links_view"></g>
            <g class="cards_view"></g>
          </g>
        </svg>`;
    const fake_cont = document.createElement("div");
    fake_cont.innerHTML = svg_html;
    const svg = fake_cont.firstElementChild;
    cont.innerHTML = "";
    cont.appendChild(svg);
    return svg;
  }
}
